/**
 * Utility functions for storing images and design data in AWS S3
 */
import { compressBase64Image } from './imageCompression';

/**
 * Helper function to determine content type from file extension
 * @param {string} fileExtension - The file extension (e.g., '.svg', '.png')
 * @returns {string} - The corresponding MIME type
 */
export const getContentTypeFromExtension = (fileExtension) => {
  switch (fileExtension.toLowerCase()) {
    case '.svg':
      return 'image/svg+xml';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    default:
      return 'image/png';
  }
};

/**
 * Helper function to detect file extension from image URL or data URL
 * @param {string} imageUrl - The image URL or data URL
 * @returns {string} - The detected file extension (e.g., '.svg', '.png')
 */
export const detectFileExtensionFromUrl = (imageUrl) => {
  try {
    if (imageUrl.startsWith('data:')) {
      // Data URL format: data:image/svg+xml;base64,... or data:image/png;base64,...
      const mimeMatch = imageUrl.match(/data:image\/([^;]+)/);
      if (mimeMatch) {
        const mimeType = mimeMatch[1];
        if (mimeType === 'svg+xml') {
          return '.svg';
        } else if (mimeType === 'jpeg') {
          return '.jpg';
        } else {
          return `.${mimeType}`;
        }
      }
    } else if (imageUrl.includes('.')) {
      // Extract extension from URL
      const urlParts = imageUrl.split('.');
      const lastPart = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
      if (['svg', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(lastPart.toLowerCase())) {
        return `.${lastPart.toLowerCase()}`;
      }
    }
  } catch (error) {
    console.warn(`Could not determine file extension from URL, using .png as fallback`);
  }
  
  return '.png'; // default fallback
};

/**
 * Upload an image to AWS S3
 * @param {string} imageSource - Image source (base64 or blob URL)
 * @param {string} key - S3 key for the image
 * @param {string} contentType - Content type of the image (e.g., 'image/png')
 * @returns {Promise<string>} - URL to the uploaded image in S3
 */
export const uploadImageToS3 = async (imageSource, key, contentType = 'image/png') => {
  try {
    console.log(`Uploading image to S3 with key: ${key}`);
    
    // Fetch the image data
    const response = await fetch(imageSource);
    const blob = await response.blob();
    
    // Convert blob to base64 data URL
    const dataUrl = await blobToDataURL(blob);
    
    // Log original size
    console.log(`Original image size: ${Math.round(dataUrl.length / 1024)} KB`);
    
    let uploadDataUrl = dataUrl;
    const isSvg = contentType === 'image/svg+xml' || key.toLowerCase().endsWith('.svg');
    
    // Skip compression for SVG files
    if (!isSvg) {
      // Compress the image - set lower max size for car images, 0.15MB is usually sufficient quality
      // Use 1000px max dimension - still good quality but smaller file size
      uploadDataUrl = await compressBase64Image(dataUrl, 0.15, 1000, false);
      
      // Log compressed size
      console.log(`Compressed image size: ${Math.round(uploadDataUrl.length / 1024)} KB`);
    } else {
      console.log('Skipping compression for SVG file');
    }
    
    // Always use the Netlify functions path for consistency
    const functionsEndpoint = '/.netlify/functions/upload-to-s3';
    
    // Determine the correct content type
    const uploadContentType = isSvg ? 'image/svg+xml' : contentType;
    
    // Call the serverless function to upload to S3
    const uploadResponse = await fetch(functionsEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file: uploadDataUrl,
        filename: key.split('/').pop(), // Extract filename from key
        type: uploadContentType,
      }),
    });
    
    // Handle unsuccessful responses
    if (!uploadResponse.ok) {
      console.error(`S3 upload failed with status: ${uploadResponse.status}`);
      
      try {
        const errorData = await uploadResponse.json();
        throw new Error(`Upload failed: ${errorData.message || errorData.error || uploadResponse.statusText}`);
      } catch (parseError) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }
    }
    
    const result = await uploadResponse.json();
    console.log(`Successfully uploaded image to S3: ${result.url}`);
    return result.url;
  } catch (error) {
    console.error('Error uploading image to S3:', error);
    
    // Fallback to local blob URL if in development environment
    if (process.env.NODE_ENV === 'development') {
      console.warn('Using local blob URL as fallback in development mode');
      return imageSource;
    }
    
    throw error;
  }
};

/**
 * Upload design data to AWS S3
 * @param {Object} designData - The design data to upload
 * @param {string} key - S3 key for the design data
 * @returns {Promise<string>} - URL to the uploaded design data in S3
 */
export const uploadDesignToS3 = async (designData, key) => {
  try {
    console.log(`Uploading design data to S3 with key: ${key}`);
    
    // Convert the JSON data to a data URL
    const jsonString = JSON.stringify(designData);
    const dataUrl = `data:application/json;base64,${btoa(jsonString)}`;
    
    // Always use the Netlify functions path for consistency
    const functionsEndpoint = '/.netlify/functions/upload-to-s3';
    
    // Call the serverless function to upload to S3
    const uploadResponse = await fetch(functionsEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file: dataUrl,
        filename: key.split('/').pop(), // Extract filename from key
        type: 'application/json',
      }),
    });
    
    // Handle unsuccessful responses
    if (!uploadResponse.ok) {
      console.error(`S3 upload failed with status: ${uploadResponse.status}`);
      
      try {
        const errorData = await uploadResponse.json();
        throw new Error(`Upload failed: ${errorData.message || errorData.error || uploadResponse.statusText}`);
      } catch (parseError) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }
    }
    
    const result = await uploadResponse.json();
    console.log(`Successfully uploaded design data to S3: ${result.url}`);
    return result.url;
  } catch (error) {
    console.error('Error uploading design to S3:', error);
    
    // In development mode, just use a dummy URL as fallback
    if (process.env.NODE_ENV === 'development') {
      console.warn('Using dummy URL as fallback in development mode');
      return `https://example.com/dummy-s3/${key}`;
    }
    
    throw error;
  }
};

/**
 * Upload design preview images to S3
 * @param {Object} previewImages - Object containing view names and image URLs
 * @param {string} baseKey - Base S3 key for the preview images
 * @returns {Promise<Object>} - Object containing view names and S3 URLs
 */
export const uploadPreviewImagesToS3 = async (previewImages, baseKey) => {
  const s3PreviewImages = {};
  
  try {
    console.log(`Uploading ${Object.keys(previewImages).length} preview images to S3 with base key: ${baseKey}`);
    
    const uploadPromises = Object.entries(previewImages).map(async ([view, imageUrl]) => {
      // Normalize filename: lowercase, replace spaces with underscores
      const filenamePart = view.toLowerCase().replace(/\s+/g, '_');
      
      // Determine file extension from the image URL or content type
      const fileExtension = detectFileExtensionFromUrl(imageUrl);
      
      // But keep the original view name as the key in the returned object
      const viewKey = `${baseKey}/${filenamePart}${fileExtension}`;
      
      // Determine content type based on file extension
      const contentType = getContentTypeFromExtension(fileExtension);
      
      try {
        const s3Url = await uploadImageToS3(imageUrl, viewKey, contentType);
        s3PreviewImages[view] = s3Url;
      } catch (error) {
        console.error(`Error uploading preview image for view "${view}":`, error);
        // Use original URL as fallback
        s3PreviewImages[view] = imageUrl;
      }
    });
    
    await Promise.all(uploadPromises);
    console.log('Uploaded all preview images with keys:', Object.keys(s3PreviewImages));
    return s3PreviewImages;
  } catch (error) {
    console.error('Error uploading preview images to S3:', error);
    throw error;
  }
};

/**
 * Helper function to convert Blob to data URL
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} - Data URL string
 */
function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Helper function to convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer - The array buffer to convert
 * @returns {string} - Base64 string
 */
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  return btoa(binary);
} 